import { FetchQueryOptions, QueryClient } from "@tanstack/react-query";
import type {
  ParamsForMethod,
  ReturnForMethod,
  StandardQueryMethodForModel,
  ModelName,
  TellescopeQueryKey,
  SupportedSession,
} from "../../types";
import { buildTellescopeQueryKey } from "../../keys";
import { executeTellescopeApiMethod } from "../execute-tellescope-api-method";
import { TellescopeDefinedQueryOptionKeys } from "../../hooks/types";

type PrefetchTellescopeQueryOptions<
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
> = Omit<
  FetchQueryOptions<ReturnForMethod<Model, Method>, TError, TData, TellescopeQueryKey<Model, Method>>,
  TellescopeDefinedQueryOptionKeys
>;

/**
 * Arguments for prefetching a Tellescope query.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TError - The error type (defaults to Error)
 * @template TData - The transformed data type (defaults to the method's return type)
 */
export interface PrefetchTellescopeQueryArgs<
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
> extends PrefetchTellescopeQueryOptions<Model, Method, TError, TData> {
  /** The Tellescope model name */
  model: Model;
  /** The query method to call on the model */
  method: Method;
  /** The request parameters for the query */
  request: ParamsForMethod<Model, Method>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** The Tellescope session instance */
  session: SupportedSession;
  requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
}

/**
 * Prefetches a query from the Tellescope API using the query client.
 * This is a lower-level function that wraps `queryClient.prefetchQuery` with Tellescope-specific configuration.
 * Prefetching allows you to fetch data before it's needed, improving perceived performance.
 *
 * @param args - The prefetch query arguments
 * @returns A promise that resolves when the query has been prefetched
 *
 * @example
 * ```tsx
 * await prefetchTellescopeQuery({
 *   queryClient,
 *   session,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 * ```
 */
export const prefetchTellescopeQuery = <
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
>({
  model,
  method,
  request,
  queryClient,
  session,
  requestKeysToOmitFromQueryKey,
  ...tanstackOptions
}: PrefetchTellescopeQueryArgs<Model, Method, TError, TData>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request, requestKeysToOmitFromQueryKey });
  return queryClient.prefetchQuery<ReturnForMethod<Model, Method>, TError, TData, TellescopeQueryKey<Model, Method>>({
    queryKey,
    queryFn: () =>
      executeTellescopeApiMethod<Model, Method, false>({
        session,
        model,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      }),
    ...tanstackOptions,
  });
};
